import type { StoreGroup, Store } from '../types/plan-template';

// Generate unique IDs
const generateId = () => Math.random().toString(36).substr(2, 9);

// Mock store groups data
export const MOCK_STORE_GROUPS: StoreGroup[] = [
  {
    id: 'SG001',
    code: 'HQ_FLAGSHIP',
    name: 'HQ Flagship Store Group',
    description: 'HQ directly operated flagship stores, located in core business districts, serving brand image display function',
    shopCodes: ['ST0001', 'ST0002', 'ST0003', 'ST0004', 'ST0005', 'ST0006', 'ST0007', 'ST0008', 'ST0009', 'ST0010', 'ST0011', 'ST0012', 'ST0013', 'ST0014', 'ST0015', 'ST0016', 'ST0017', 'ST0018', 'ST0019', 'ST0020', 'ST0021', 'ST0022', 'ST0023', 'ST0024', 'ST0025']
  },
  {
    id: 'SG002',
    code: 'STANDARD_EAST',
    name: 'East China Standard Store Group',
    description: 'Standard stores in East China region, covering business areas in major cities',
    shopCodes: Array.from({ length: 180 }, (_, i) => `ST${String(i + 26).padStart(4, '0')}`)
  },
  {
    id: 'SG003',
    code: 'STANDARD_SOUTH',
    name: 'South China Standard Store Group',
    description: 'Standard stores in South China region, mainly distributed in Guangdong, Fujian and other provinces',
    shopCodes: Array.from({ length: 150 }, (_, i) => `ST${String(i + 206).padStart(4, '0')}`)
  },
  {
    id: 'SG004',
    code: 'COMMUNITY_EAST',
    name: 'East China Community Store Group',
    description: 'Community convenience stores in East China region, serving daily shopping needs of surrounding residents',
    shopCodes: Array.from({ length: 320 }, (_, i) => `ST${String(i + 356).padStart(4, '0')}`)
  },
  {
    id: 'SG005',
    code: 'COMMUNITY_SOUTH',
    name: 'South China Community Store Group',
    description: 'Community convenience stores in South China region, focusing on localized product configuration',
    shopCodes: Array.from({ length: 280 }, (_, i) => `ST${String(i + 676).padStart(4, '0')}`)
  },
  {
    id: 'SG006',
    code: 'PREMIUM_NORTH',
    name: 'North China Premium Store Group',
    description: 'Premium stores in North China region, mainly serving high-consumption groups',
    shopCodes: Array.from({ length: 45 }, (_, i) => `ST${String(i + 956).padStart(4, '0')}`)
  },
  {
    id: 'SG007',
    code: 'STANDARD_NORTH',
    name: 'North China Standard Store Group',
    description: 'Standard stores in North China region, covering Beijing, Tianjin, Hebei and other areas',
    shopCodes: Array.from({ length: 200 }, (_, i) => `ST${String(i + 1001).padStart(4, '0')}`)
  },
  {
    id: 'SG008',
    code: 'OUTLET_WEST',
    name: 'Western China Outlet Store Group',
    description: 'Outlet stores in Western China region, mainly selling discounted products',
    shopCodes: Array.from({ length: 35 }, (_, i) => `ST${String(i + 1201).padStart(4, '0')}`)
  },
  {
    id: 'SG009',
    code: 'STANDARD_WEST',
    name: 'Western China Standard Store Group',
    description: 'Standard stores in Western China region, adapted to local consumption habits',
    shopCodes: Array.from({ length: 120 }, (_, i) => `ST${String(i + 1236).padStart(4, '0')}`)
  },
  {
    id: 'SG010',
    code: 'CENTRAL_MIX',
    name: 'Central China Mixed Store Group',
    description: 'Comprehensive stores in Central China region with rich product variety',
    shopCodes: Array.from({ length: 95 }, (_, i) => `ST${String(i + 1356).padStart(4, '0')}`)
  }
];

// Generate mock stores
const generateMockStores = (): Store[] => {
  const stores: Store[] = [];
  const areas = ['East China', 'South China', 'North China', 'Western China', 'Central China'];
  const storeFormats = ['Flagship Store', 'Standard Store', 'Community Store', 'Premium Store', 'Outlet Store'];
  const clusterSections = ['Large', 'Medium', 'Small', 'Mini'];
  const cities = {
    'East China': ['Shanghai', 'Hangzhou', 'Nanjing', 'Suzhou', 'Ningbo', 'Wenzhou', 'Hefei', 'Fuzhou'],
    'South China': ['Guangzhou', 'Shenzhen', 'Dongguan', 'Foshan', 'Zhuhai', 'Xiamen', 'Quanzhou', 'Nanning'],
    'North China': ['Beijing', 'Tianjin', 'Shijiazhuang', 'Taiyuan', 'Hohhot', 'Shenyang', 'Dalian', 'Changchun'],
    'Western China': ['Chengdu', 'Chongqing', 'Xi\'an', 'Lanzhou', 'Urumqi', 'Kunming', 'Guiyang', 'Lhasa'],
    'Central China': ['Wuhan', 'Changsha', 'Zhengzhou', 'Nanchang', 'Jinan', 'Qingdao', 'Yantai', 'Weifang']
  };

  let storeIndex = 1;

  areas.forEach(area => {
    const areaCities = cities[area as keyof typeof cities];
    const storeCount = Math.floor(Math.random() * 50) + 30; // 30-80 stores per area

    for (let i = 0; i < storeCount; i++) {
      const city = areaCities[Math.floor(Math.random() * areaCities.length)];
      const format = storeFormats[Math.floor(Math.random() * storeFormats.length)];
      const clusterSection = clusterSections[Math.floor(Math.random() * clusterSections.length)];
      const storeCode = `ST${String(storeIndex).padStart(4, '0')}`;

      stores.push({
        id: generateId(),
        code: storeCode,
        name: `${city} ${format} ${storeIndex}`,
        area,
        format,
        clusterSection,
        detailAddress: `${area} ${city} Business Street ${Math.floor(Math.random() * 999) + 1}`
      });

      storeIndex++;
    }
  });

  return stores;
};

export const MOCK_STORES = generateMockStores();

// Export stores by area
export const STORES_BY_AREA: Record<string, Store[]> = {
  'East China': MOCK_STORES.filter(s => s.area === 'East China'),
  'South China': MOCK_STORES.filter(s => s.area === 'South China'),
  'North China': MOCK_STORES.filter(s => s.area === 'North China'),
  'Western China': MOCK_STORES.filter(s => s.area === 'Western China'),
  'Central China': MOCK_STORES.filter(s => s.area === 'Central China')
};

// Export stores by format
export const STORES_BY_FORMAT: Record<string, Store[]> = {
  'Flagship Store': MOCK_STORES.filter(s => s.format === 'Flagship Store'),
  'Standard Store': MOCK_STORES.filter(s => s.format === 'Standard Store'),
  'Community Store': MOCK_STORES.filter(s => s.format === 'Community Store'),
  'Premium Store': MOCK_STORES.filter(s => s.format === 'Premium Store'),
  'Outlet Store': MOCK_STORES.filter(s => s.format === 'Outlet Store')
};

// Export stores by cluster section
export const STORES_BY_CLUSTER_SECTION: Record<string, Store[]> = {
  'Large': MOCK_STORES.filter(s => s.clusterSection === 'Large'),
  'Medium': MOCK_STORES.filter(s => s.clusterSection === 'Medium'),
  'Small': MOCK_STORES.filter(s => s.clusterSection === 'Small'),
  'Mini': MOCK_STORES.filter(s => s.clusterSection === 'Mini')
};

// Export store statistics
export const STORE_STATISTICS = {
  totalStores: MOCK_STORES.length,
  storesByArea: Object.keys(STORES_BY_AREA).map(area => ({
    area,
    count: STORES_BY_AREA[area].length
  })),
  storesByFormat: Object.keys(STORES_BY_FORMAT).map(format => ({
    format,
    count: STORES_BY_FORMAT[format].length
  })),
  storesByClusterSection: Object.keys(STORES_BY_CLUSTER_SECTION).map(clusterSection => ({
    clusterSection,
    count: STORES_BY_CLUSTER_SECTION[clusterSection].length
  }))
};